import { useCallback } from 'react'
import { useDokumententresorEncryptionConfig } from '.'
import { Check, CheckResult } from '../api'

export const useDokumententresorCryptoSubtle = (): Check => {
    const config = useDokumententresorEncryptionConfig()
    const testContent = 'Testinhalt'
    const enc = new TextEncoder()
    const dec = new TextDecoder()

    const generateKeys = useCallback(async (): Promise<Required<CryptoKeyPair>> => {
        const generatedKeyPair = await crypto.subtle.generateKey(config, true, ['encrypt', 'decrypt'])
        if (!generatedKeyPair.privateKey) throw new Error('Privater Schlüssel konnte nicht erstellt werden')
        if (!generatedKeyPair.publicKey) throw new Error('Öffentlicher Schlüssel konnte nicht erstellt werden')
        return { privateKey: generatedKeyPair.privateKey, publicKey: generatedKeyPair.publicKey }
    }, [config])

    return {
        id: 'dokumententresor_cryptsubtle',
        name: 'Kryptographische API',
        link: 'https://developer.mozilla.org/en-US/docs/Web/API/Crypto/subtle',
        description: 'Testet, ob der Browser die benötigten APIs zur Verfügung stellt.',
        func: () => {
            return new Promise<CheckResult>((resolve) => {
                const run = async () => {
                    try {
                        const generatedKeyPair: Required<CryptoKeyPair> = await generateKeys()

                        const encryptedContent = await crypto.subtle.encrypt(config, generatedKeyPair.publicKey, enc.encode(testContent))
                        const exportedPrivateKey = await crypto.subtle.exportKey('jwk', generatedKeyPair.privateKey)
                        const exportedPublicKey = await crypto.subtle.exportKey('jwk', generatedKeyPair.publicKey)
                        const importedKeyPair = {
                            privateKey: await crypto.subtle.importKey('jwk', exportedPrivateKey, { name: config.name, hash: { name: config.hash } }, true, [
                                'decrypt',
                            ]),
                            publicKey: await crypto.subtle.importKey('jwk', exportedPublicKey, { name: config.name, hash: { name: config.hash } }, true, [
                                'encrypt',
                            ]),
                        }
                        const decryptedContent = await crypto.subtle.decrypt(config, importedKeyPair.privateKey, encryptedContent)
                        if (dec.decode(decryptedContent) !== testContent) throw new Error()
                        const canCrytoSubtle = crypto && crypto.subtle
                        return resolve(
                            canCrytoSubtle
                                ? { status: 'success' }
                                : { status: 'error', msg: 'Ihr Browser unterstützt die benötigten Verschlüsselung-APIs nicht.' }
                        )
                    } catch (e) {
                        return resolve({ status: 'error', msg: (e as { message: string }).message })
                    }
                }
                run()
            })
        },
    }
}
